import { useState } from 'react';
import { Check, X, RotateCcw, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import api from '../../services/api';

export default function ExercisePlayer({ exercise }) {
    const [answer, setAnswer] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [loading, setLoading] = useState(false);

    if (!exercise) return null;

    const handleCheck = async () => {
        setLoading(true);
        try {
            const res = await api.post(`/lesson-exercises/${exercise.id}/check`, { answer });
            setFeedback(res.data);
        } catch (e) {
            setFeedback({ is_correct: false, message: e.response?.data?.message || 'Tekshirishda xatolik' });
        }
        setLoading(false);
    };

    const reset = () => {
        setAnswer(null);
        setFeedback(null);
    };

    const renderInput = () => {
        switch (exercise.type) {
            case 'calculation':
                return (
                    <input
                        type="text"
                        value={answer || ''}
                        onChange={(e) => setAnswer(e.target.value)}
                        placeholder={exercise.data?.placeholder || 'Javobingizni kiriting'}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                );

            case 'ordering': {
                const items = exercise.data?.items || [];
                const order = answer || items.map((i) => i.id);
                const move = (index, direction) => {
                    const newOrder = [...order];
                    const target = index + direction;
                    if (target < 0 || target >= newOrder.length) return;
                    [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
                    setAnswer(newOrder);
                };
                return (
                    <div className="space-y-2">
                        {order.map((id, idx) => {
                            const item = items.find((i) => i.id === id);
                            return (
                                <div key={id} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 px-4 py-3 rounded-xl border border-gray-100 dark:border-gray-700">
                                    <span className="text-sm font-medium text-gray-500 w-6">{idx + 1}.</span>
                                    <span className="flex-1 text-sm text-gray-800 dark:text-gray-200">{item?.text}</span>
                                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-30">
                                        <ArrowUp className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => move(idx, 1)} disabled={idx === order.length - 1} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-30">
                                        <ArrowDown className="w-4 h-4" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            case 'matching': {
                const left = exercise.data?.left || [];
                const right = exercise.data?.right || [];
                const pairs = answer || {};
                return (
                    <div className="space-y-3">
                        {left.map((l) => (
                            <div key={l.id} className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <span className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm text-gray-800 dark:text-gray-200 border border-gray-100 dark:border-gray-700">
                                    {l.text}
                                </span>
                                <select
                                    value={pairs[l.id] || ''}
                                    onChange={(e) => setAnswer({ ...pairs, [l.id]: e.target.value })}
                                    className="flex-1 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Tanlang...</option>
                                    {right.map((r) => (
                                        <option key={r.id} value={r.id}>{r.text}</option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                );
            }

            default:
                return <p className="text-sm text-gray-500">Bu topshiriq turi hali qo'llab-quvvatlanmaydi.</p>;
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-6">
            <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 font-medium">
                    Interaktiv topshiriq
                </span>
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">{exercise.title}</h3>
            {exercise.prompt && <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{exercise.prompt}</p>}

            {renderInput()}

            {feedback && (
                <div className={`mt-4 px-4 py-3 rounded-xl text-sm flex items-start gap-2 ${
                    feedback.is_correct
                        ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                        : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                }`}>
                    {feedback.is_correct ? <Check className="w-4 h-4 mt-0.5" /> : <X className="w-4 h-4 mt-0.5" />}
                    {feedback.message}
                </div>
            )}

            <div className="flex items-center gap-3 mt-4">
                <button
                    onClick={handleCheck}
                    disabled={loading || answer === null || answer === ''}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-600 text-white rounded-xl text-sm font-medium hover:bg-cyan-700 disabled:opacity-50 transition"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Tekshirish
                </button>
                <button
                    onClick={reset}
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                    <RotateCcw className="w-4 h-4" /> Qayta boshlash
                </button>
            </div>
        </div>
    );
}
